<template>
    <div class="h-100 d-flex flex-column">
        <div class="border-top p-4 flex-1 d-flex flex-column overflow-auto">
            <div class="d-flex align-items-center mb-3">
                <h6 class="m-0 size-16 dark-text">{{ $t('res_Title_AutoForwardRules') }}</h6>
                <v-spacer></v-spacer>
                <v-btn color="primary" dense @click="openAddDialog">
                    <v-icon left>mdi-plus</v-icon>
                    {{ $t('res_ButtonText_AddRule') }}
                </v-btn>
            </div>

            <v-data-table
                :mobile-breakpoint="0"
                class="position-relative white flex-1 d-flex overflow-auto"
                disable-sort
                :headers="headers"
                :items="rules"
                :no-data-text="$t('res_Msgs_NoAutoForwardRules')"
                :hide-default-footer="rules.length <= 10"
                :items-per-page="10"
                header-fixed
                fixed-header>
                <template v-slot:item="row">
                    <tr>
                        <td>{{ row.item.to }}</td>
                        <td>
                            <v-chip small :color="row.item.type === 'To' ? 'primary' : 'secondary'" outlined>
                                {{ row.item.type }}
                            </v-chip>
                        </td>
                        <td>
                            <template v-if="row.item.exceptionUsers && row.item.exceptionUsers.length">
                                <v-chip
                                    v-for="user in row.item.exceptionUsers.slice(0, 3)"
                                    :key="user.id + '-' + user.structureId"
                                    x-small
                                    class="mr-1">
                                    {{ user.name }}
                                </v-chip>
                                <span v-if="row.item.exceptionUsers.length > 3" class="text-caption">
                                    +{{ row.item.exceptionUsers.length - 3 }}
                                </span>
                            </template>
                            <span v-else class="text-caption">—</span>
                        </td>
                        <td>{{ formatDate(row.item.createdDate) }}</td>
                        <td class="text-start">
                            <div class="onhover-display">
                                <div class="custom-icon-wrapper">
                                    <v-icon @click="openEditDialog(row.item)">custom-icon white-bg edit-icon</v-icon>
                                </div>
                                <div class="custom-icon-wrapper">
                                    <v-icon @click="confirmDelete(row.item)">custom-icon white-bg delete-icon</v-icon>
                                </div>
                            </div>
                        </td>
                    </tr>
                </template>
            </v-data-table>
        </div>

        <v-dialog v-model="editDialog" width="640" persistent>
            <v-card>
                <v-toolbar class="shadow-none" content-class="h-auto">
                    <v-toolbar-title class="size-16 dark-text">
                        {{ editingRule.id ? $t('res_Label_EditRule') : $t('res_ButtonText_AddRule') }}
                    </v-toolbar-title>
                    <v-spacer></v-spacer>
                    <v-icon @click="editDialog = false">mdi-close</v-icon>
                </v-toolbar>

                <div class="border-top p-4">
                    <v-row>
                        <v-col cols="12" sm="8">
                            <label>{{ $t('res_Label_ForwardTo') }}<span> *</span></label>
                            <multiselect
                                class="light-bg"
                                v-model="editingRule.structure"
                                :append-to-body="true"
                                label="name"
                                track-by="id"
                                open-direction="bottom"
                                :placeholder="$t('res_Label_SelectOption')"
                                :options="structures"
                                :searchable="true"
                                :loading="loading"
                                :internal-search="false"
                                :clear-on-select="true"
                                :close-on-select="true"
                                :options-limit="300"
                                :max-height="600"
                                :show-no-results="false"
                                :hide-selected="false"
                                :multiple="false"
                                @search-change="asyncFindForwardStructure"
                                @close="structures = []">
                                <template slot="option" slot-scope="props">
                                    <div style="height: 50px; width: 100%; display: flex">
                                        <div>
                                            <v-avatar color="#eff2f5" size="40">{{ capitalizeFirstLetter(props.option.name) }}</v-avatar>
                                        </div>
                                        <div class="ad__box">
                                            <p class="ad__text__bold">{{ props.option.name }}</p>
                                            <p class="ad__text">{{ getMultiSelectSubHeaderDisplay(props.option) }}</p>
                                        </div>
                                    </div>
                                </template>
                                <template slot="noOptions">{{ $t('res_Msgs_EmptySearch') }}</template>
                            </multiselect>
                        </v-col>
                        <v-col cols="12" sm="4">
                            <label>{{ $t('res_Label_Type') }}<span> *</span></label>
                            <v-select
                                v-model="editingRule.type"
                                :items="typeOptions"
                                item-text="label"
                                item-value="value"
                                solo
                                dense
                                hide-details></v-select>
                        </v-col>
                    </v-row>

                    <v-row>
                        <v-col cols="12">
                            <label>{{ $t('res_Label_Exceptions') }}</label>
                            <multiselect
                                class="light-bg"
                                v-model="editingRule.exceptionUsers"
                                :append-to-body="true"
                                label="name"
                                track-by="id"
                                open-direction="bottom"
                                :placeholder="$t('res_Lable_SelectExceptionUsers')"
                                :options="exceptionUsersOptions"
                                :searchable="true"
                                :loading="loading"
                                :internal-search="false"
                                :clear-on-select="true"
                                :close-on-select="false"
                                :options-limit="300"
                                :max-height="600"
                                :show-no-results="false"
                                :hide-selected="true"
                                :multiple="true"
                                @search-change="asyncFindExceptionUser"
                                @close="exceptionUsersOptions = []">
                                <template slot="option" slot-scope="props">
                                    <div style="height: 50px; width: 100%; display: flex">
                                        <div>
                                            <v-avatar color="#eff2f5" size="40">{{ capitalizeFirstLetter(props.option.name) }}</v-avatar>
                                        </div>
                                        <div class="ad__box">
                                            <p class="ad__text__bold">{{ props.option.name }}</p>
                                            <p class="ad__text">{{ getMultiSelectSubHeaderDisplay(props.option) }}</p>
                                        </div>
                                    </div>
                                </template>
                                <template slot="noOptions">{{ $t('res_Msgs_EmptySearch') }}</template>
                            </multiselect>
                        </v-col>
                    </v-row>

                    <div class="actions d-flex mt-3">
                        <v-btn color="primary" dense @click="saveRule">{{ $t('res_ButtonText_Save') }}</v-btn>
                        <v-btn color="light-blue" dense class="ms-2" @click="editDialog = false">{{ $t('res_ButtonText_Cancel') }}</v-btn>
                    </div>
                </div>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import axios from 'axios'
import constants from '@/assets/js/constants'
import Multiselect from 'vue-multiselect'
import * as utils from '@/assets/js/common.js'

export default {
    name: 'AutoForward',
    components: { Multiselect },
    props: {
        structureId: Number,
    },
    data() {
        return {
            rules: [],
            structures: [],
            exceptionUsersOptions: [],
            loading: false,
            editDialog: false,
            editingRule: this.makeEmptyRule(),
            typeOptions: [
                { value: 'To', label: 'To' },
                { value: 'Cc', label: 'Cc' },
            ],
            headers: [
                { text: this.$t('res_Label_ForwardTo'), value: 'to' },
                { text: this.$t('res_Label_Type'), value: 'type', width: '100px' },
                { text: this.$t('res_Label_Exceptions'), value: 'exceptions' },
                { text: this.$t('res_Label_CreatedDate'), value: 'createdDate', width: '160px' },
                { text: '', value: 'actions', sortable: false, width: '90px' },
            ],
        }
    },
    watch: {
        structureId(newVal, oldVal) {
            if (newVal !== oldVal) {
                this.loadRules()
            }
        },
    },
    mounted() {
        this.loadRules()
    },
    methods: {
        makeEmptyRule() {
            // first item is a placeholder hidden by global CSS rule
            // `.multiselect__tag:first-child { display: none; }` in main.css.
            // real selections start at index 1.
            return {
                id: null,
                structure: null,
                type: 'To',
                exceptionUsers: [{ id: '0', name: '' }],
            }
        },
        loadRules() {
            if (!this.structureId) {
                this.rules = []
                return
            }
            axios.post(constants.baseApiUrl + 'AutoForward/GetAutoForwardRulesForStructure?structureId=' + this.structureId)
                .then(res => {
                    this.rules = Array.isArray(res.data) ? res.data : []
                })
                .catch(err => console.warn(err))
        },
        openAddDialog() {
            this.editingRule = this.makeEmptyRule()
            this.editDialog = true
        },
        openEditDialog(rule) {
            this.editingRule = {
                id: rule.id,
                structure: { id: rule.toStructureId, name: rule.to },
                type: rule.type || 'To',
                exceptionUsers: [
                    { id: '0', name: '' },
                    ...(rule.exceptionUsers || []).map(u => ({
                        id: `${u.id}-${u.structureId}`,
                        name: u.name,
                        isUser: true,
                    })),
                ],
            }
            this.editDialog = true
        },
        saveRule() {
            if (!this.editingRule.structure || !this.editingRule.structure.id) {
                window.showAlert('res_Msgs_FillRequired', 'warning')
                return
            }
            if (!this.editingRule.type) {
                window.showAlert('res_Msgs_FillRequired', 'warning')
                return
            }

            const exceptionIds = (this.editingRule.exceptionUsers || [])
                .map(u => u.id)
                .filter(id => id !== undefined && id !== null && id !== '' && id !== '0')
                .join(',')

            const payload = {
                Id: this.editingRule.id || null,
                Type: this.editingRule.type,
                ExceptionIds: exceptionIds,
                FromStructureId: this.structureId,
                ToStructureId: this.editingRule.structure.id,
            }

            const isUpdate = !!this.editingRule.id
            const url = constants.baseApiUrl + (isUpdate ? 'AutoForward/UpdateAutoForwardRule' : 'AutoForward/AddNewAutoForwardRule')

            window.showLoading()
            axios.post(url, payload)
                .then(res => {
                    if (res.data === false) {
                        window.showAlert('res_Msgs_AutoForwardRuleDuplicate', 'error')
                        return
                    }
                    window.showAlert(isUpdate ? 'res_Msgs_AutoForwardRuleUpdated' : 'res_Msgs_AutoForwardRuleAdded', 'success')
                    this.editDialog = false
                    this.loadRules()
                })
                .catch(err => console.warn(err))
                .finally(() => window.hideLoading())
        },
        confirmDelete(rule) {
            window.showInformation(
                this.$t('res_Msg_DeleteConfirmation'),
                this.$t('res_Msgs_DeleteRuleConfirmation'),
                () => {
                    window.showLoading()
                    axios.post(constants.baseApiUrl + 'AutoForward/DeleteAutoForwardRule?autoForwardId=' + rule.id)
                        .then(res => {
                            if (res.data === true) {
                                window.showAlert('res_Msgs_AutoForwardRuleDeleted', 'success')
                                this.loadRules()
                            }
                        })
                        .catch(err => console.warn(err))
                        .finally(() => window.hideLoading())
                },
                () => {}
            )
        },
        asyncFindForwardStructure(query) {
            const url = 'StructureUser/search?showUsers=false&showInternalStructures=true&showExternalStructures=false&search='
            axios.get(constants.baseApiUrl + url + (query || ''), { headers: constants.headers })
                .then(res => { this.structures = res.data || [] })
                .catch(err => console.warn(err))
        },
        asyncFindExceptionUser(query) {
            const url = 'StructureUser/search?showUsers=true&showInternalStructures=false&showExternalStructures=false&search='
            axios.get(constants.baseApiUrl + url + (query || ''), { headers: constants.headers })
                .then(res => { this.exceptionUsersOptions = res.data || [] })
                .catch(err => console.warn(err))
        },
        formatDate(value) {
            if (!value) return ''
            try {
                return utils.dateFormat(value, 'dd/MM/yy HH:mm')
            } catch (e) {
                return new Date(value).toLocaleString()
            }
        },
        capitalizeFirstLetter(str) {
            return utils.CapitalizeFirstLetter(str)
        },
        getMultiSelectSubHeaderDisplay(option) {
            return utils.GetMultiSelectSubHeaderDisplay(option)
        },
    },
}
</script>
